/**
 * ENG-8428: the canonicalisation behind `agent_tool_calls.input_sha256`.
 *
 * WHY THIS LIVES IN CORE. This digest is written on the host by the CLI
 * extractor (ENG-8573, `apps/cli/src/lib/tool-call-redaction.ts`) and verified
 * in the API by the transcript retrieval route — two packages that cannot
 * import each other. A second, hand-copied implementation would have to agree
 * with the first byte-for-byte forever, and the failure direction is the worst
 * available: a drifted canonicaliser makes every genuine transcript line fail
 * verification, so the route reports intact evidence as `integrity_mismatch`.
 * At an incident review that reads as "the platform says its own audit trail
 * was tampered with" — strictly worse than serving nothing.
 *
 * So the two consumers share one implementation, enforced by the compiler
 * rather than by a parity guard script. The CLI re-exports from here; do not
 * reintroduce a local copy in either package.
 *
 * NODE-ONLY. Uses `node:crypto`, so this is a subpath export
 * (`@augmented/core/tool-calls/input-hash.js`) and deliberately NOT part of the
 * browser-safe barrel — the same carve-out `@augmented/core/crypto` uses.
 */
import { createHash } from 'node:crypto';

/**
 * sha256 (hex) of the canonicalised tool input, for `input_sha256`.
 *
 * Proves a retrieved transcript line is the one a row indexes, without storing
 * the arguments. Keys are sorted so two structurally identical inputs hash
 * alike regardless of serialisation order. This is a digest of CONTENT and is
 * safe to emit only because it is one-way over a high-entropy input — unlike a
 * bare path hash, a tool input is not drawn from a small guessable space.
 *
 * Returns null for an absent or unserialisable input. A null digest means "no
 * claim", never "verified": a caller must treat it as unverifiable rather than
 * as a match.
 */
export function hashToolInput(input: unknown): string | null {
  if (input === undefined) return null;
  try {
    return createHash('sha256').update(canonicalJson(input)).digest('hex');
  } catch {
    // Circular or otherwise unserialisable input — no digest rather than a
    // partial one, which would fail verification later and look like tampering.
    return null;
  }
}

/** Deterministic JSON: object keys sorted at every depth. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}
